import { useEffect, useMemo, useState } from "react";
import "./admin-email.css";
import { MdOutlineAdd } from "react-icons/md";
import { FaSearch } from "react-icons/fa";
import { HashLoader } from "react-spinners";
import ToastNotification, { showToast } from "../../components/toasts/ToastNotification";
import AddEmailModal from "../../components/modal/add-email-modal";
import { MdDelete, MdMarkEmailUnread } from "react-icons/md";
import { deleteEmailForAdmin, getAllEmailsForAdmin } from "../../api/gmail.api";
import ConfirmModal from "../../components/modal/confirm-modal";
import { useNavigate } from "react-router-dom";
import { FaCopy } from "react-icons/fa6";
import { hasPermission } from "../../utils/auth";
import { useUser } from "../../context/UserContext";
import Pagination from "../../components/pagination/Pagination";
import { useDebounce } from "../../hooks/useDebounce";

const ITEMS_PER_PAGE = 8;

export const AdminEmail = () => {
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearchInput = useDebounce(searchInput, 500);

  const [emails, setEmails] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEmails, setTotalEmails] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIdToDelete, setSelectedIdToDelete] = useState("");
  const navigate = useNavigate();
  const { user } = useUser();

  useEffect(() => {
    document.title = "Admin - Email";
  }, []);

  useEffect(() => {
    fetchEmails();
  }, [currentPage, debouncedSearchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchInput]);

  const summary = useMemo(() => {
    return {
      currentPageCount: emails.length,
      copiedReady: emails.filter((em) => em.email && em.password).length,
    };
  }, [emails]);

  const fetchEmails = async (showLoader = true) => {
    try {
      if (showLoader) setIsFetching(true);
      const response = await getAllEmailsForAdmin(
        ITEMS_PER_PAGE,
        currentPage,
        debouncedSearchInput.trim()
      );

      if (response.success) {
        setEmails(response.data.emails || []);
        setTotalPages(response.data.totalPages || 1);
        setTotalEmails(response.data.total || 0);
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      if (showLoader) setIsFetching(false);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const handleDeleteEmail = async () => {
    if (isActionLoading) return;
    try {
      setIsActionLoading(true);
      setIsDeleteModalOpen(false);
      const res = await deleteEmailForAdmin(selectedIdToDelete);

      if (res.success) {
        showToast("Xóa email thành công", "success");
        await fetchEmails(false);
        setSelectedIdToDelete("");
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleCopy = async (text) => {
    await navigator.clipboard.writeText(text);
    showToast("Đã copy dữ liệu", "success");
  };

  return (
    <div className="admin-email-page">
      <ToastNotification />
      <AddEmailModal
        title="Thêm Email"
        message="Email và mật khẩu sẽ được tạo tự động"
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        setEmails={setEmails}
      />

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onConfirm={handleDeleteEmail}
        onClose={() => setIsDeleteModalOpen(false)}
        message={"Xác nhận bạn đang xóa một email"}
        title={"Xóa email"}
      />

      <div className="admin-email-shell">
        <div className="admin-email-header">
          <div>
            <p className="admin-email-breadcrumb">Quản trị / Email</p>
            <h1 className="admin-email-title">Danh sách email</h1>
          </div>
          <div className="admin-email-kpi">
            <div className="admin-email-kpi-item">
              <span>Tổng email</span>
              <strong>{totalEmails}</strong>
            </div>
            <div className="admin-email-kpi-item">
              <span>Trang hiện tại</span>
              <strong>{summary.currentPageCount}</strong>
            </div>
            <div className="admin-email-kpi-item">
              <span>Sẵn sàng dùng</span>
              <strong>{summary.copiedReady}</strong>
            </div>
          </div>
        </div>

        <div className="admin-email-toolbar">
          <div className="admin-email-search-wrap">
            <FaSearch className="admin-email-search-icon" />
            <input
              type="text"
              placeholder="Tìm kiếm theo email"
              className="admin-email-search"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
          {hasPermission(user?.role, "create:email") && (
            <button
              type="button"
              className="admin-email-add-button"
              onClick={() => setIsAddModalOpen(true)}
            >
              <MdOutlineAdd className="admin-email-add-icon" />
              <span>Thêm email mới</span>
            </button>
          )}
        </div>

        {isFetching && emails.length === 0 ? (
          <div className="loader-container">
            <HashLoader color="var(--hash-loader-color)" />
          </div>
        ) : (
          <>
            <div className="admin-email-table-wrap">
              <table className="admin-email-table">
                <thead>
                  <tr>
                    <th>STT</th>
                    <th>ID</th>
                    <th>Email</th>
                    <th>Mật khẩu</th>
                    <th>Thời gian</th>
                    <th>Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {emails.length > 0 ? (
                    emails.map((email, index) => (
                      <tr key={email._id}>
                        <td className="admin-email-cell-muted">{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                        <td className="admin-email-cell-code">{email._id}</td>
                        <td>
                          <div className="admin-email-copy-row">
                            <span>{email.email}</span>
                            <button
                              type="button"
                              onClick={() => handleCopy(email.email)}
                              className="admin-email-copy-btn"
                              title="Copy email"
                            >
                              <FaCopy />
                            </button>
                          </div>
                        </td>
                        <td>
                          <div className="admin-email-copy-row">
                            <span>{email.password}</span>
                            <button
                              type="button"
                              onClick={() => handleCopy(email.password)}
                              className="admin-email-copy-btn"
                              title="Copy mật khẩu"
                            >
                              <FaCopy />
                            </button>
                          </div>
                        </td>
                        <td className="admin-email-cell-muted">{new Date(email.createdAt).toLocaleString("vi-VN")}</td>
                        <td>
                          <div className="admin-email-actions">
                            <button
                              onClick={() => navigate(`/super-admin/emails/view/${email.email}`)}
                              className="admin-email-action admin-email-action-view"
                              title="Xem thư"
                              disabled={isActionLoading}
                            >
                              <MdMarkEmailUnread />
                            </button>
                            {hasPermission(user?.role, "delete:email") && (
                              <button
                                onClick={() => {
                                  setSelectedIdToDelete(email._id);
                                  setIsDeleteModalOpen(true);
                                }}
                                className="admin-email-action admin-email-action-delete"
                                title="Xóa email"
                                disabled={isActionLoading}
                              >
                                <MdDelete />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6}>
                        <p className="admin-email-empty">Không có email phù hợp bộ lọc.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPrev={handlePrevPage}
                onNext={handleNextPage}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};
